import React, { useMemo, useState, Fragment } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { BsFiletypeCsv, BsChevronRight } from 'react-icons/bs';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import useExportCSV from '../../hooks/useExportCSV';

export type ReferralStats = {
  confirmed: number;
  pending: number;
  expired: number;
  total: number;
  totalEarnings: number;
};

export type PersonNode = {
  referrerUserId?: number; // Main identifier for API calls
  refereeUserId?: number; // Legacy support
  name: string | null;
  phone: string;
  email?: string;
  latestReferralDate?: string;
  directReferrals?: ReferralStats; // Direct referrals stats
  organizationReferrals?: ReferralStats; // Organization (total) referrals stats
  // Legacy fields for backward compatibility
  confirmed?: number;
  pending?: number;
  expired?: number;
  total?: number;
  totalEarnings?: number;
  hasChildren?: boolean;
  children?: PersonNode[];
  isLoading?: boolean;
};

// Default demo data

// Get organization stats (handle both API formats)
function getOrganizationStats(node: PersonNode): ReferralStats {
  if (node.organizationReferrals) {
    return {
      confirmed: parseInt(String(node.organizationReferrals.confirmed)) || 0,
      pending: parseInt(String(node.organizationReferrals.pending)) || 0,
      expired: parseInt(String(node.organizationReferrals.expired)) || 0,
      total: parseInt(String(node.organizationReferrals.total)) || 0,
      totalEarnings: node.organizationReferrals.totalEarnings || 0,
    };
  }
  // For legacy API format
  return {
    confirmed: node.confirmed || 0,
    pending: node.pending || 0,
    expired: node.expired || 0,
    total: node.total || 0,
    totalEarnings: node.totalEarnings || 0,
  };
}

// Get direct referrals stats (handle both API formats)
function getDirectReferrals(node: PersonNode): ReferralStats {
  if (node.directReferrals) {
    return {
      confirmed: parseInt(String(node.directReferrals.confirmed)) || 0,
      pending: parseInt(String(node.directReferrals.pending)) || 0,
      expired: parseInt(String(node.directReferrals.expired)) || 0,
      total: parseInt(String(node.directReferrals.total)) || 0,
      totalEarnings: node.directReferrals.totalEarnings || 0,
    };
  }
  // For legacy API format - treat as direct since it's leaf level
  return {
    confirmed: node.confirmed || 0,
    pending: node.pending || 0,
    expired: node.expired || 0,
    total: node.total || 0,
    totalEarnings: node.totalEarnings || 0,
  };
}

// Get unique ID for a node
function getNodeId(node: PersonNode): number {
  const id = node.referrerUserId || node.refereeUserId || 0;
  console.log('getNodeId for node:', node.name, 'ID:', id);
  return id;
}

function nodeHasChildren(node: PersonNode): boolean {
  if (typeof node.hasChildren === 'boolean') {
    return node.hasChildren;
  }
  return (
    (node.organizationReferrals?.total &&
      node.organizationReferrals.total > 0) ||
    false
  );
}

/**
 * Enhanced Row component with improved visual hierarchy for tree structure
 *
 * Visual improvements:
 * - Increased indentation (32px per level) for better parent-child distinction
 * - Added connecting lines (vertical and horizontal) to show tree relationships
 * - Progressive background colors (blue → indigo → purple) by depth level
 * - Left border indicators with different colors per level
 * - Enhanced toggle buttons with visual indicators for leaf nodes
 * - Improved typography hierarchy (font weights and sizes by depth)
 */
function Row({
  node,
  depth,
  isOpen,
  toggle,
  zebra,
  onLoadChildren,
}: {
  node: PersonNode;
  depth: number;
  isOpen: (id: number) => boolean;
  toggle: (id: number) => void;
  zebra: boolean;
  onLoadChildren?: (userId: number) => Promise<void>;
}) {
  const nodeId = getNodeId(node);
  const hasLoadedChildren = !!node.children?.length;
  const canHaveChildren = nodeHasChildren(node);
  const canExpand = hasLoadedChildren || canHaveChildren;
  const orgStats = useMemo(() => getOrganizationStats(node), [node]);
  const directStats = useMemo(() => getDirectReferrals(node), [node]);

  const handleToggle = async () => {
    if (nodeId === 0) return; // Safety check

    if (!hasLoadedChildren && canHaveChildren && !isOpen(nodeId)) {
      // Load children first if they don't exist
      if (onLoadChildren && node.referrerUserId) {
        await onLoadChildren(node.referrerUserId);
      }
    }
    toggle(nodeId);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    } else if (e.key === 'ArrowRight' && canExpand && !isOpen(nodeId)) {
      e.preventDefault();
      handleToggle();
    } else if (e.key === 'ArrowLeft' && canExpand && isOpen(nodeId)) {
      e.preventDefault();
      toggle(nodeId);
    }
  };

  return (
    <Fragment>
      {/* Desktop view */}
      <tr
        className={[
          'hidden md:table-row',
          depth === 0
            ? zebra
              ? 'bg-white'
              : 'bg-gray-50'
            : depth === 1
              ? zebra
                ? 'bg-blue-25 border-l-4 border-l-blue-400'
                : 'bg-blue-50 border-l-4 border-l-blue-400'
              : depth === 2
                ? zebra
                  ? 'bg-indigo-25 border-l-4 border-l-indigo-400'
                  : 'bg-indigo-50 border-l-4 border-l-indigo-400'
                : zebra
                  ? 'bg-purple-25 border-l-4 border-l-purple-400'
                  : 'bg-purple-50 border-l-4 border-l-purple-400',
          'border-b border-gray-200 hover:bg-gray-100 transition-colors',
          depth > 0 ? 'shadow-sm' : '',
        ].join(' ')}
      >
        <td
          className={`px-6 py-4 w-80 max-w-80 ${depth > 0 ? `ml-${depth * 8}` : ''}`}
        >
          <div className='flex items-center'>
            {/* Enhanced parent level indentation - increased spacing */}
            <div
              style={{ width: depth * 40, marginLeft: depth * 16 }}
              aria-hidden
            />

            {/* Visual tree structure indicators */}
            {depth > 0 && (
              <div className='flex items-center mr-2'>
                {/* Vertical connecting line */}
                <div className='w-px h-6 bg-gray-300 mr-2' />
                {/* Horizontal connecting line */}
                <div className='w-4 h-px bg-gray-300' />
              </div>
            )}

            {/* Toggle button area - always same width */}
            <div className='w-6 flex justify-start mr-2 flex-shrink-0'>
              {canExpand ? (
                <button
                  onClick={handleToggle}
                  onKeyDown={handleKeyDown}
                  aria-expanded={isOpen(nodeId)}
                  aria-controls={`row-children-${nodeId}`}
                  className='inline-flex items-center justify-center w-5 h-5 rounded-sm border border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white hover:bg-gray-100 shadow-sm'
                  title={isOpen(nodeId) ? 'Collapse' : 'Expand'}
                  disabled={node.isLoading}
                >
                  {node.isLoading ? (
                    <div className='w-3 h-3 border border-blue-600 rounded-full animate-spin border-t-transparent' />
                  ) : (
                    <BsChevronRight
                      className={`w-3 h-3 transition-transform ${isOpen(nodeId) ? 'rotate-90' : 'rotate-0'}`}
                    />
                  )}
                </button>
              ) : (
                <div className='w-5 h-5 flex items-center justify-center'>
                  {depth > 0 && (
                    <div className='w-1.5 h-1.5 bg-gray-400 rounded-full' />
                  )}
                </div>
              )}
            </div>

            <span
              className={`font-medium hover:underline cursor-default ${
                depth === 0
                  ? 'text-gray-900 font-bold text-base'
                  : depth === 1
                    ? 'text-gray-700 font-semibold text-sm'
                    : 'text-gray-600 font-medium text-sm'
              }`}
            >
              {node.name || node.email}
            </span>
          </div>
        </td>
        <td className='px-4 py-4 w-32 max-w-32 text-sm border-r border-gray-300'>
          {node.phone}
        </td>
        {/* Direct referrals */}
        <td className='px-2 py-4 w-16 max-w-16 truncate font-semibold text-center text-sm'>
          {directStats.confirmed}
        </td>
        <td className='px-2 py-4 w-16 max-w-16 truncate text-center text-sm'>
          {directStats.pending}
        </td>
        <td className='px-2 py-4 w-16 max-w-16 truncate text-center text-sm'>
          {directStats.expired}
        </td>
        <td className='px-2 py-4 w-16 max-w-16 truncate font-bold text-center text-sm bg-blue-50 border-r border-gray-300'>
          {directStats.confirmed + directStats.pending + directStats.expired}
        </td>
        {/* Organization referrals */}
        <td className='px-2 py-4 w-16 max-w-16 truncate font-semibold text-center text-sm'>
          {orgStats.confirmed}
        </td>
        <td className='px-2 py-4 w-16 max-w-16 truncate text-center text-sm'>
          {orgStats.pending}
        </td>
        <td className='px-2 py-4 w-16 max-w-16 truncate text-center text-sm'>
          {orgStats.expired}
        </td>
        <td className='px-2 py-4 w-16 max-w-16 truncate font-bold text-center text-sm bg-blue-50'>
          {orgStats.confirmed + orgStats.pending + orgStats.expired}
        </td>
      </tr>

      {/* Mobile view */}
      <tr
        className={[
          'md:hidden border-b',
          depth === 0
            ? zebra
              ? 'bg-white'
              : 'bg-gray-50'
            : depth === 1
              ? zebra
                ? 'bg-blue-25 border-l-4 border-l-blue-400'
                : 'bg-blue-50 border-l-4 border-l-blue-400'
              : depth === 2
                ? zebra
                  ? 'bg-indigo-25 border-l-4 border-l-indigo-400'
                  : 'bg-indigo-50 border-l-4 border-l-indigo-400'
                : zebra
                  ? 'bg-purple-25 border-l-4 border-l-purple-400'
                  : 'bg-purple-50 border-l-4 border-l-purple-400',
          'hover:bg-gray-100 transition-colors',
          depth > 0 ? 'shadow-sm' : '',
        ].join(' ')}
      >
        <td className={`p-4 w-full ${depth > 0 ? `ml-${depth * 6}` : ''}`}>
          <div className='flex items-center mb-2'>
            {/* Enhanced parent level indentation - increased spacing */}
            <div
              style={{ width: depth * 32, marginLeft: depth * 12 }}
              aria-hidden
            />

            {/* Visual tree structure indicators */}
            {depth > 0 && (
              <div className='flex items-center mr-2'>
                {/* Vertical connecting line */}
                <div className='w-px h-6 bg-gray-300 mr-2' />
                {/* Horizontal connecting line */}
                <div className='w-3 h-px bg-gray-300' />
              </div>
            )}

            {/* Toggle button area - always same width */}
            <div className='w-6 flex justify-start mr-2 flex-shrink-0'>
              {canExpand ? (
                <button
                  onClick={handleToggle}
                  onKeyDown={handleKeyDown}
                  aria-expanded={isOpen(nodeId)}
                  aria-controls={`row-children-${nodeId}`}
                  className='inline-flex items-center justify-center w-5 h-5 rounded-sm border border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white hover:bg-gray-100 shadow-sm'
                  title={isOpen(nodeId) ? 'Collapse' : 'Expand'}
                  disabled={node.isLoading}
                >
                  {node.isLoading ? (
                    <div className='w-3 h-3 border border-blue-600 rounded-full animate-spin border-t-transparent' />
                  ) : (
                    <BsChevronRight
                      className={`w-3 h-3 transition-transform ${isOpen(nodeId) ? 'rotate-90' : 'rotate-0'}`}
                    />
                  )}
                </button>
              ) : (
                <div className='w-5 h-5 flex items-center justify-center'>
                  {depth > 0 && (
                    <div className='w-1.5 h-1.5 bg-gray-400 rounded-full' />
                  )}
                </div>
              )}
            </div>

            <span
              className={`font-medium ${
                depth === 0
                  ? 'text-gray-900 font-bold text-base'
                  : depth === 1
                    ? 'text-gray-700 font-semibold text-sm'
                    : 'text-gray-600 font-medium text-sm'
              }`}
            >
              {node.name || node.email}
            </span>
          </div>

          <div className='text-sm text-gray-600 mb-2'>{node.phone}</div>

          <div className='grid grid-cols-2 gap-4 text-sm'>
            <div>
              <div className='font-medium text-gray-700 mb-1'>
                Direct Referrals
              </div>
              <div className='space-y-1'>
                <div>
                  Confirmed:{' '}
                  <span className='font-semibold'>{directStats.confirmed}</span>
                </div>
                <div>Pending: {directStats.pending}</div>
                <div>Expired: {directStats.expired}</div>
                <div className='border-t pt-1 mt-1'>
                  <span className='font-bold text-blue-600'>
                    Total:{' '}
                    {directStats.confirmed +
                      directStats.pending +
                      directStats.expired}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <div className='font-medium text-gray-700 mb-1'>Organization</div>
              <div className='space-y-1'>
                <div>
                  Confirmed:{' '}
                  <span className='font-semibold'>{orgStats.confirmed}</span>
                </div>
                <div>Pending: {orgStats.pending}</div>
                <div>Expired: {orgStats.expired}</div>
                <div className='border-t pt-1 mt-1'>
                  <span className='font-bold text-blue-600'>
                    Total:{' '}
                    {orgStats.confirmed + orgStats.pending + orgStats.expired}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </td>
      </tr>

      {hasLoadedChildren && isOpen(nodeId) && (
        <>
          {node.children!.map(child => (
            <Row
              key={getNodeId(child)}
              node={child}
              depth={depth + 1}
              isOpen={isOpen}
              toggle={toggle}
              zebra={!zebra}
              onLoadChildren={onLoadChildren}
            />
          ))}
        </>
      )}
    </Fragment>
  );
}

// TreeTable component props interface
export interface TreeTableProps {
  data?: PersonNode[];
  title?: string;
  showExportButton?: boolean;
  dataFilter?: boolean;
  customCSVExporting?: any;
  onLoadChildren?: (userId: number) => Promise<PersonNode[]>;
  filterBy?: string;
  // Server-side pagination props
  serverSideMode?: boolean;
  totalCount?: number;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onSortChange?: (sortBy: string) => void;
  loading?: boolean;
  rowsPerPage?: number;
}

export default function TreeTableDemo({
  data = [],
  title = 'Contacts — Tree Table',
  showExportButton = false,
  dataFilter = false,
  customCSVExporting,
  onLoadChildren,
  filterBy = 'alltime',
  // Server-side pagination props
  serverSideMode = false,
  totalCount = 0,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onSortChange,
  loading = false,
  rowsPerPage = 10,
}: TreeTableProps) {
  console.log('TreeTable rec data:', data);
  const [treeData, setTreeData] = useState<PersonNode[]>(data);
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [searchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('1 year');
  const [customDateRange, setCustomDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: new Date('2000-01-01'),
    to: new Date('3073-12-31'),
  });
  const [isForCSVLoading, setIsForCSVLoading] = useState(false);
  const handleExportCSV = useExportCSV();

  // Update tree data when prop changes
  React.useEffect(() => {
    console.log('TreeTable received data:', data);
    setTreeData(data);
  }, [data]);

  const isOpen = (id: number) => !!open[id];
  const toggle = (id: number) => setOpen(s => ({ ...s, [id]: !s[id] }));

  // Handle loading children
  const handleLoadChildren = async (userId: number): Promise<void> => {
    if (!onLoadChildren) return;

    // Set loading state
    setTreeData(prevData =>
      updateNodeInTree(prevData, userId, { isLoading: true })
    );

    try {
      const children = await onLoadChildren(userId);

      // Update the node with children
      setTreeData(prevData =>
        updateNodeInTree(prevData, userId, { children, isLoading: false })
      );
    } catch (error) {
      console.error('Failed to load children:', error);
      setTreeData(prevData =>
        updateNodeInTree(prevData, userId, { isLoading: false })
      );
    }
  };

  // Helper function to update a node in the tree
  const updateNodeInTree = (
    nodes: PersonNode[],
    targetId: number,
    updates: Partial<PersonNode>
  ): PersonNode[] => {
    return nodes.map(node => {
      const nodeId = getNodeId(node);
      if (nodeId === targetId) {
        return { ...node, ...updates };
      }
      if (node.children) {
        return {
          ...node,
          children: updateNodeInTree(node.children, targetId, updates),
        };
      }
      return node;
    });
  };

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDateFilter(e.target.value);
  };

  const handleCustomDateChange = (from: Date | null, to: Date | null) => {
    setCustomDateRange({
      from: from || new Date('2000-01-01'),
      to: to || new Date('3023-12-31'),
    });
  };

  // Filter data based on search query
  const filteredData = useMemo(() => {
    console.log('TreeTable treeData:', treeData);
    console.log('TreeTable searchQuery:', searchQuery);
    if (!searchQuery) {
      console.log('Returning treeData directly:', treeData);
      return treeData;
    }

    const filterNode = (node: PersonNode): PersonNode | null => {
      const filteredChildren = node.children
        ?.map(filterNode)
        .filter(Boolean) as PersonNode[] | undefined;

      if (filteredChildren && filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren,
        };
      }

      return null;
    };

    return treeData.map(filterNode).filter(Boolean) as PersonNode[];
  }, [treeData, searchQuery]);

  const exportCSV = async (forCSV = false) => {
    if (forCSV) {
      setIsForCSVLoading(true);

      // Flatten tree structure to include all nodes (parents and children)
      const flattenTreeData = (nodes: PersonNode[], level = 0): any[] => {
        const result: any[] = [];

        nodes.forEach(node => {
          // Add current node with level indicator
          const orgStats = getOrganizationStats(node);
          const directStats = getDirectReferrals(node);
          result.push({
            name: '  '.repeat(level) + (node.name || node.email), // Add indentation to show hierarchy
            phone: node.phone,
            email: node.email,
            level: level,
            confirmed_direct: directStats.confirmed,
            pending_direct: directStats.pending,
            expired_direct: directStats.expired,
            confirmed_total: orgStats.confirmed,
            pending_total: orgStats.pending,
            expired_total: orgStats.expired,
            total_direct:
              directStats.confirmed + directStats.pending + directStats.expired,
            total_organization:
              orgStats.confirmed + orgStats.pending + orgStats.expired,
          });

          // Add children recursively
          if (node.children && node.children.length > 0) {
            result.push(...flattenTreeData(node.children, level + 1));
          }
        });

        return result;
      };

      const flatData = flattenTreeData(filteredData);

      const config = {
        headers: [
          { key: 'name', label: 'Name' },
          { key: 'phone', label: 'Phone' },
          { key: 'email', label: 'Email' },
          { key: 'level', label: 'Level' },
          { key: 'confirmed_direct', label: 'Direct Confirmed' },
          { key: 'pending_direct', label: 'Direct Pending' },
          { key: 'expired_direct', label: 'Direct Expired' },
          { key: 'total_direct', label: 'Direct Total' },
          { key: 'confirmed_total', label: 'Organization Confirmed' },
          { key: 'pending_total', label: 'Organization Pending' },
          { key: 'expired_total', label: 'Organization Expired' },
          { key: 'total_organization', label: 'Organization Total' },
        ],
      };

      await handleExportCSV(config, flatData, title);
      setIsForCSVLoading(false);
    }
  };

  console.log('TreeTable final check - data.length:', data.length);
  console.log(
    'TreeTable final check - filteredData.length:',
    filteredData.length
  );

  if (data.length === 0)
    return <div className='text-center py-10'>No data available</div>;

  return (
    <div className='relative sm:rounded-lg rounded-lg border'>
      {/* Header Component - matching Table.tsx design */}
      <div className='bg-primary w-full flex flex-col sm:flex-row items-center justify-between rounded-t-lg p-4 gap-y-4'>
        <div className='flex items-center gap-x-4 flex-wrap w-full'>
          <h2 className='text-white font-semibold mb-3 sm:mb-0 text-xl'>
            {title}
          </h2>
        </div>
        {showExportButton && (
          <div className='w-full sm:w-auto flex flex-col sm:flex-row items-center gap-3'>
            <button
              onClick={async () => {
                if (customCSVExporting) {
                  setIsForCSVLoading(true);
                  await customCSVExporting(true);
                  setIsForCSVLoading(false);
                } else {
                  exportCSV(true);
                }
              }}
              className='rounded-lg w-full sm:w-auto text-black border flex px-4 py-1.5 items-center justify-center bg-white'
              disabled={isForCSVLoading}
            >
              <span>{isForCSVLoading ? 'Exporting...' : 'Export'}</span>
              <BsFiletypeCsv className='ml-2' size={22} />
            </button>
          </div>
        )}
      </div>

      {/* Date Filter Section - matching Table.tsx */}
      {dataFilter && (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 bg-gray-50'>
          <div className='flex items-center gap-2 text-nowrap'>
            <label>Date Filter:</label>
            <select
              value={dateFilter}
              onChange={handleDateFilterChange}
              className='border rounded p-2 w-40'
            >
              <option value='3 months'>Last 3 months</option>
              <option value='6 months'>Last 6 months</option>
              <option value='9 months'>Last 9 months</option>
              <option value='1 year'>Last 1 year</option>
              <option value='Custom'>Custom</option>
            </select>
          </div>

          {dateFilter === 'Custom' && (
            <div className='flex items-center'>
              <div className='flex items-center mr-4'>
                <label className='mr-2'>From:</label>
                <DatePicker
                  selected={customDateRange.from}
                  onChange={date =>
                    handleCustomDateChange(date, customDateRange.to)
                  }
                  dateFormat='dd/MM/yyyy'
                  className='border rounded p-2'
                />
              </div>
              <div className='flex items-center'>
                <label className='mr-2'>To:</label>
                <DatePicker
                  selected={customDateRange.to}
                  onChange={date =>
                    handleCustomDateChange(customDateRange.from, date)
                  }
                  dateFormat='dd/MM/yyyy'
                  className='border rounded p-2'
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className='overflow-x-auto w-full'>
        <table className='w-full min-w-fit text-sm text-left rtl:text-right text-gray-500'>
          <thead className='text-xs text-gray-700 bg-gray-50 sticky top-0 z-10'>
            {/* Desktop headers */}
            <tr className='hidden md:table-row'>
              <th
                scope='col'
                className='px-6 py-3 sticky top-0 bg-gray-50 w-80'
                rowSpan={2}
              >
                Name
              </th>
              <th
                scope='col'
                className='px-4 py-3 sticky top-0 bg-gray-50 w-32 border-r border-gray-300'
                rowSpan={2}
              >
                Phone
              </th>
              <th
                scope='col'
                className='px-6 py-3 text-center border-l border-gray-300 sticky top-0 bg-gray-50'
                colSpan={4}
              >
                Direct Referrals
              </th>
              <th
                scope='col'
                className='px-6 py-3 text-center border-l border-gray-300 sticky top-0 bg-gray-50'
                colSpan={4}
              >
                Organization
              </th>
            </tr>
            <tr className='hidden md:table-row bg-gray-100'>
              <th
                scope='col'
                className='px-2 py-3 text-center sticky top-0 bg-gray-100 w-16 text-xs'
              >
                Confirmed
              </th>
              <th
                scope='col'
                className='px-2 py-3 text-center sticky top-0 bg-gray-100 w-16 text-xs'
              >
                Pending
              </th>
              <th
                scope='col'
                className='px-2 py-3 text-center sticky top-0 bg-gray-100 w-16 text-xs'
              >
                Expired
              </th>
              <th
                scope='col'
                className='px-2 py-3 text-center sticky top-0 bg-gray-100 w-16 text-xs font-semibold border-r border-gray-300'
              >
                Total
              </th>
              <th
                scope='col'
                className='px-2 py-3 text-center sticky top-0 bg-gray-100 w-16 text-xs'
              >
                Confirmed
              </th>
              <th
                scope='col'
                className='px-2 py-3 text-center sticky top-0 bg-gray-100 w-16 text-xs'
              >
                Pending
              </th>
              <th
                scope='col'
                className='px-2 py-3 text-center sticky top-0 bg-gray-100 w-16 text-xs'
              >
                Expired
              </th>
              <th
                scope='col'
                className='px-2 py-3 text-center sticky top-0 bg-gray-100 w-16 text-xs font-semibold'
              >
                Total
              </th>
            </tr>

            {/* Mobile header */}
            <tr className='md:hidden'>
              <th
                scope='col'
                className='px-6 py-3 text-center sticky top-0 bg-gray-50'
              >
                Member Referral Tree
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((node, idx) => (
              <Row
                key={`${getNodeId(node)}-${idx}`}
                node={node}
                depth={0}
                isOpen={isOpen}
                toggle={toggle}
                zebra={idx % 2 === 0}
                onLoadChildren={handleLoadChildren}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls - only show for server-side mode */}
      {serverSideMode && totalPages > 1 && (
        <div className='flex items-center justify-between bg-white px-4 py-3 sm:px-6'>
          <div className='flex flex-1 justify-between sm:hidden'>
            <button
              onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
                currentPage === 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                onPageChange?.(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
                currentPage === totalPages
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
          <div className='hidden sm:flex sm:flex-1 sm:items-center sm:justify-between'>
            <div>
              <p className='text-sm text-gray-700'>
                Showing{' '}
                <span className='font-medium'>
                  {(currentPage - 1) * rowsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className='font-medium'>
                  {Math.min(currentPage * rowsPerPage, totalCount)}
                </span>{' '}
                of <span className='font-medium'>{totalCount}</span> results
              </p>
            </div>
            <div>
              <nav
                className='isolate inline-flex -space-x-px rounded-md shadow-sm'
                aria-label='Pagination'
              >
                <button
                  onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                    currentPage === 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-400'
                  }`}
                >
                  <span className='sr-only'>Previous</span>
                  <FaChevronLeft
                    className={`${
                      currentPage !== 1 ? 'hover:text-gray-500' : ''
                    }`}
                  />
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Show pages around current page
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange?.(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        currentPage === pageNum
                          ? 'z-10 bg-primary text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() =>
                    onPageChange?.(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                    currentPage === totalPages
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-400'
                  }`}
                >
                  <span className='sr-only'>Next</span>
                  <FaChevronRight
                    className={`${
                      currentPage !== totalPages ? 'hover:text-gray-500' : ''
                    }`}
                  />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
